import { useState, useEffect } from 'react';
import { getTrending, getMovieDetails, getTvDetails } from '../services/tmdb';
import { useStore } from '../store/useStore';
import HeroBanner from '../components/media/HeroBanner';
import MediaRow from '../components/media/MediaRow';
import OnePieceWidget from '../components/watchlist/OnePieceWidget';
import HomeShapeStrip from '../components/chimera/HomeShapeStrip';
import DiscoveryPortal from '../components/chimera/DiscoveryPortal';
import HomeCatalogBody from '../components/home/HomeCatalogBody';
import { CURATED_PICKS } from '../data/curatedPicks';
import type { Movie, TvShow } from '../types';

export default function Home() {
  const [heroMedia, setHeroMedia] = useState<Movie | TvShow | null>(null);
  const [trending, setTrending] = useState<(Movie | TvShow)[]>([]);
  const [curatedMovies, setCuratedMovies] = useState<(Movie | TvShow)[]>([]);
  const [loading, setLoading] = useState(true);

  const { continueWatching, konami } = useStore();

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      const curatedPromise = Promise.allSettled(
        CURATED_PICKS.map((pick) =>
          pick.type === 'movie' ? getMovieDetails(pick.tmdbId) : getTvDetails(pick.tmdbId)
        )
      );

      try {
        setLoading(true);

        const trendingData = await getTrending('all', 'week');
        if (cancelled) return;

        const heroPool = trendingData.results.slice(0, 10);
        const heroData = heroPool[Math.floor(Math.random() * heroPool.length)] ?? null;
        setHeroMedia(heroData);
        setTrending(trendingData.results.slice(0, 20));
        setLoading(false);

        // Curated details begin with the main request but settle independently,
        // so they never hold the hero or the unified catalog behind a waterfall.
        const curatedResults = await curatedPromise;
        if (cancelled) return;
        setCuratedMovies(
          curatedResults
            .filter((r): r is PromiseFulfilledResult<Movie | TvShow> => r.status === 'fulfilled')
            .map((r) => r.value)
        );
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={`min-h-screen ${konami ? 'party-mode' : ''}`}>
      {/* Hero Banner — cross-fades through the top trending heroes every 7s */}
      <HeroBanner media={heroMedia} heroPool={trending.slice(0, 5)} loading={loading} />

      {/* Content Rows */}
      <div className="-mt-20 relative z-10 pb-12 space-y-10 lg:space-y-12">
        {/* Returning-viewer shape acknowledgment — substrate-meet-consciousness at entry */}
        <HomeShapeStrip />

        {/* Continue Watching */}
        {continueWatching.length > 0 && (
          <MediaRow
            title="Continue Watching"
            items={continueWatching}
            showProgress={true}
          />
        )}

        <HomeCatalogBody />

        <DiscoveryPortal />

        {curatedMovies.length > 0 && (
          <MediaRow title="Captain's Picks" items={curatedMovies} />
        )}

        {/* One Piece Widget - THE GOAT */}
        <div className="px-4 sm:px-6 lg:px-12 py-4">
          <OnePieceWidget />
        </div>

      </div>

      {/* Fun Easter Egg Message */}
      {konami && (
        <div className="fixed bottom-4 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm animate-bounce">
          🎉 You found the secret!
        </div>
      )}
    </div>
  );
}
